// agent-rows/hooks.ts — the ONE owner of the `['agent', 'rows', …]` cache key.
//
// THE RULE: a query key root is declared in exactly one module.
//
// A React Query key is not a string. It is the pair (key, stored shape). Two
// modules that write the same key declare two shapes for one cache entry, and
// the surface that mounts first decides which shape every other surface then
// reads. The reader gets fields that are silently absent.
//
// That defect class has shipped twice already: SEP-165 (a full-page 500) and
// commit f03b22f5, where a key FACTORY existed and did not stop it, because a
// factory owns the key and not the shape. The cure both times was one owning
// module. The third occurrence was this family: `['agent', 'rows',
// 'call_extracted_facts']` held summary rows, or raw rows, or narrowed rows,
// by mount order. This module is the owner that ends it.
// `apps/web/src/lib/__tests__/query-key-ownership.test.ts` keeps a second
// declaration out.
//
// THE STORED SHAPE: `useAgentRows` below is private. It stores the row exactly
// as `/api/agent/rows/:table` gives it, because that row is lossless. Each
// public hook then narrows the stored row with React Query `select`, which
// changes what the caller reads and never what the cache holds. A new
// projection is therefore a new exported hook here, and never a second key.
//
// THE POLL: ONE interval for the whole key, and no per-caller option. This is
// a deliberate choice, not an oversight of the move.
//
// A poll interval is not part of the stored shape, so the ownership rule above
// does not decide it. React Query does. It runs a refetch timer PER OBSERVER,
// so several observers on one key give the shortest live timer, and the real
// cadence then depends on which panels happen to be open. Before this module
// the same key carried 10-second and 30-second observers at once for exactly
// that reason. An `interval` option would keep the module's interface honest
// about each caller's old value and hand the ambiguity straight back: the
// Client Graph (`useCallFacts`) and the review body read the SAME key, so two
// callers with two intervals still give one undefined cadence. One interval,
// stated here, is the only answer a reader can rely on. It also keeps the
// interface at "a table in, rows out".
//
// WHY 30 SECONDS IS RIGHT FOR EVERY CALLER. A human decision never waits for
// the poll: every write path invalidates `['agent', 'rows']` at once (the
// review bodies and the composition workbench both do). The poll has one job
// left, which is to catch a BACKGROUND agent run that lands a new artefact. An
// agent run takes minutes, so a 10-second poll buys no freshness a person can
// see, and it triples the load on `/api/agent/rows/:table`, which returns the
// whole table on each call. 30 seconds was already the cadence of the busiest
// group, the review bodies.
//
// WHAT THIS CHANGED. Three callers polled at 10 seconds before the move and
// now poll at 30: the Client Graph, the KB-notes panel, and the performance
// snapshots on the monthly-narrative panel. `useArtefactSummary` in
// `use-review-queue.ts` declared no interval at all and now polls at 30, which
// is a refresh it never had. No caller lost a refresh it could act on.

import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AgentRowTable, ArtefactSummaryRow } from '@septima/contract';
import { api } from '@/lib/api';
import type { BaseDraft } from '@/data/types';
import {
    toArtefactSummary,
    toCallActionItem,
    toCallFact,
    toCallKbNote,
    toCoacActionDraft,
    toComposerDraft,
    toMonthlyNarrativeDraft,
    toPerformanceSnapshot,
    type AgentRowRecord,
    type CallActionItemRow,
    type CallFactRow,
    type CallKbNoteRow,
    type CoacActionDraftRow,
    type MonthlyNarrativeDraftRow,
    type PortfolioPerformanceSnapshotRow,
} from './rows';

export type {
    AgentRowRecord,
    AgentRowReviewStatus,
    CallActionItemRow,
    CallFactRow,
    CallKbNoteRow,
    CoacActionDraftRow,
    MonthlyNarrativeDraftRow,
    MonthlyNarrativeVariant,
    PortfolioPerformanceSnapshotRow,
} from './rows';

/** The key root this module owns. Read it for an invalidation; never write a
 *  second declaration of it. */
export const AGENT_ROWS_KEY_ROOT = ['agent', 'rows'] as const;

/** The key segment for "no table". `useArtefactSummaries` takes a null table
 *  for a task that has no artefact pointer, and the query then stays idle. */
const NO_TABLE = '<none>';

/** How often every agent-row hook refreshes. One key, one policy: see "THE
 *  POLL" in the header for why this is not a per-caller option. */
const REFETCH_INTERVAL_MS = 30_000;

/**
 * The private transport. It holds the key AND the stored shape: the loose row
 * the endpoint gives, unchanged. Nothing outside this module may call it, and
 * nothing outside this module may name its key.
 */
function useAgentRows<T>(table: AgentRowTable | null, project: (row: AgentRowRecord) => T): T[] {
    // `project` is a module-level function, so this `select` keeps one
    // identity. React Query then re-runs it only when the stored rows change,
    // and each caller gets a stable array to put in a `useMemo` dependency.
    const select = useCallback((rows: AgentRowRecord[]): T[] => rows.map(project), [project]);
    const { data } = useQuery({
        queryKey: ['agent', 'rows', table ?? NO_TABLE],
        queryFn: async (): Promise<AgentRowRecord[]> => {
            if (!table) return [];
            const res = await api.agent.listAgentRows({ params: { table }, query: {} });
            if (res.status !== 200) throw new Error(`fetch ${res.status}`);
            return res.body.rows;
        },
        // `select` reshapes what this caller reads. The cache keeps the loose
        // row, so the next caller narrows the same row to its own shape.
        select,
        enabled: table != null,
        refetchInterval: REFETCH_INTERVAL_MS,
    });
    return data ?? [];
}

/**
 * The summary line of each artefact in one table. The review queue reads only
 * the common content scalars, so this projection keeps only those.
 * A null table gives an empty list and issues no request.
 */
export function useArtefactSummaries(table: AgentRowTable | null): ArtefactSummaryRow[] {
    return useAgentRows(table, toArtefactSummary);
}

/** Every ATLAS extracted fact (`call_extracted_facts`). */
export function useCallFacts(): CallFactRow[] {
    return useAgentRows('call_extracted_facts', toCallFact);
}

/** Every ATLAS action item (`call_action_items`). */
export function useCallActionItems(): CallActionItemRow[] {
    return useAgentRows('call_action_items', toCallActionItem);
}

/** Every ATLAS knowledge-base note (`call_kb_notes`). */
export function useCallKbNotes(): CallKbNoteRow[] {
    return useAgentRows('call_kb_notes', toCallKbNote);
}

/** Every monthly-narrative draft (`wealth_monthly_narrative_drafts`). */
export function useMonthlyNarrativeDrafts(): MonthlyNarrativeDraftRow[] {
    return useAgentRows('wealth_monthly_narrative_drafts', toMonthlyNarrativeDraft);
}

/** Every portfolio performance snapshot
 *  (`wealth_portfolio_performance_snapshots`). */
export function usePerformanceSnapshots(): PortfolioPerformanceSnapshotRow[] {
    return useAgentRows('wealth_portfolio_performance_snapshots', toPerformanceSnapshot);
}

/** Every COAC action draft (`coac_action_drafts`). */
export function useCoacActionDrafts(): CoacActionDraftRow[] {
    return useAgentRows('coac_action_drafts', toCoacActionDraft);
}

/** Every composer draft in one `*Draft` table, as the workbench envelope. */
export function useComposerDrafts(table: AgentRowTable): BaseDraft[] {
    return useAgentRows(table, toComposerDraft);
}
